import {
    type ReactElement,
    useEffect,
    Fragment,
    useRef
} from "react";
import {
    type NativeSyntheticEvent,
    type NativeScrollEvent,
    Platform,
    View
} from "react-native";
import type ISelectPopoverProps from "./type";
import stylesheet, {
    SELECT_POPOVER_SCROLL_END_THRESHOLD,
    getSelectPopoverIsScrollEnd,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    SelectedItem
} from "../../type";
import {
    getSelectionGroups
} from "../../../../helpers/selection";
import {
    formatNumber
} from "../../../../helpers/locale";
import {
    getComboboxMatch
} from "../../../combobox/util";
import PopoverSurface from "../../../popoverSurface";
import OptionRow from "../../../optionRow";
import Button from "../../../button";
import Text from "../../../text";

const preventActionFocusSteal = (event: {
    preventDefault: () => void;
}) => {
    event.preventDefault();
};

function SelectPopover<T>({
    isMaxChoiceReached,
    mainSelectedItems,
    moreLoadThreshold,
    isMultipleSelect,
    onSearchKeyPress,
    renderOptionIcon,
    setIsMoreLoading,
    isClearDisabled,
    customLocalize,
    groupExtractor,
    isMinChoiceMet,
    onActiveChange,
    returnFocusRef,
    isMoreLoading,
    setSearchText,
    isSearchable,
    setIsLoading,
    customTheme,
    getOptionID,
    isShowTools,
    onMoreLoad,
    renderItem,
    searchText,
    activeKey,
    anchorRef,
    isLoading,
    listboxID,
    maxChoice,
    onDismiss,
    searchRef,
    onSelect,
    onApply,
    onClear,
    options,
    isOpen,
    title,
    data
}: ISelectPopoverProps<T>) {
    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isScrollEndReachedRef = useRef(false);

    const query = searchText.trim();

    const selectedKeys = new Set(mainSelectedItems.map((item) => item.__key));

    const {
        footer: footerDynamicStyle
    } = useStyles({
        spaces
    });

    useEffect(() => {
        isScrollEndReachedRef.current = false;
    }, [
        options.length
    ]);

    const webProps = Platform.OS === "web" ? {
        onMouseDown: preventActionFocusSteal
    } : {};

    const onListScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        if(!onMoreLoad) {
            return;
        }

        const {
            layoutMeasurement,
            contentOffset,
            contentSize
        } = event.nativeEvent;

        const isScrollEnd = getSelectPopoverIsScrollEnd({
            threshold: moreLoadThreshold ?? SELECT_POPOVER_SCROLL_END_THRESHOLD,
            layoutHeight: layoutMeasurement.height,
            contentHeight: contentSize.height,
            offset: contentOffset.y
        });

        if(!isScrollEnd) {
            isScrollEndReachedRef.current = false;

            return;
        }

        if(isScrollEndReachedRef.current || isMoreLoading) {
            return;
        }

        isScrollEndReachedRef.current = true;

        onMoreLoad({
            selectedItems: mainSelectedItems,
            setIsMoreLoading,
            setIsLoading,
            searchText,
            data
        });
    };

    const getFormattedCount = (value: number) => {
        return formatNumber({
            locale: activeLocale,
            value
        });
    };

    const getCountText = () => {
        if(maxChoice !== undefined && maxChoice >= 0) {
            return localize("selected-count-with-max", [
                getFormattedCount(mainSelectedItems.length),
                getFormattedCount(maxChoice)
            ]);
        }

        return localize("selected-options-with-count", [
            getFormattedCount(mainSelectedItems.length)
        ]);
    };

    const renderLabel = (item: SelectedItem) => {
        const match = query.length ? getComboboxMatch({
            locale: activeLocale,
            title: item.__title,
            query
        }) : undefined;

        if(!match) {
            return item.__title;
        }

        return <Fragment>
            {item.__title.slice(0, match.start)}
            <OptionRow.Highlight
                customTheme={customTheme}
            >
                {item.__title.slice(match.start, match.end)}
            </OptionRow.Highlight>
            {item.__title.slice(match.end)}
        </Fragment>;
    };

    const renderOption = (item: T & SelectedItem | SelectedItem, index: number) => {
        const isSelected = selectedKeys.has(item.__key);

        if(renderItem) {
            return renderItem({
                selectedItems: mainSelectedItems,
                setIsMoreLoading,
                key: item.__key,
                setIsLoading,
                isSelected,
                searchText,
                onSelect,
                index,
                item,
                data
            });
        }

        return <OptionRow
            onHoverIn={isMultipleSelect && !isSelected && isMaxChoiceReached ? undefined : () => onActiveChange?.(item.__key)}
            isHoverSuppressed={activeKey !== undefined && item.__key !== activeKey}
            isDisabled={isMultipleSelect && !isSelected && isMaxChoiceReached}
            leading={renderOptionIcon ? renderOptionIcon({
                index,
                item
            }) : undefined}
            isActive={item.__key === activeKey}
            onPress={() => onSelect(item)}
            isMultiple={isMultipleSelect}
            id={getOptionID(item.__key)}
            label={renderLabel(item)}
            customTheme={customTheme}
            isShowActiveRing={false}
            isSelected={isSelected}
            key={item.__key}
        />;
    };

    const getItemGroup = (item: T & SelectedItem | SelectedItem) => groupExtractor ? groupExtractor(item) ?? "" : "";

    const renderGroups = () => {
        let offset = 0;

        const groups = getSelectionGroups({
            getHeading: (item) => getItemGroup(item) || undefined,
            getKey: getItemGroup,
            entries: options
        });

        return groups.map((group, groupIndex) => {
            const previousGroup = groups[groupIndex - 1];

            const previousKey = previousGroup?.options[previousGroup.options.length - 1]?.__key;

            const nextKey = group.options[0]?.__key;

            const groupOffset = offset;

            offset += group.options.length;

            const rows = group.options.map((item, itemIndex) => renderOption(item, groupOffset + itemIndex));

            return <Fragment
                key={`${group.heading ?? ""}-${nextKey}`}
            >
                {
                    previousGroup ?
                        <PopoverSurface.Separator
                            isHighlighted={previousKey === activeKey || nextKey === activeKey}
                            customTheme={customTheme}
                        />
                    :
                        null
                }
                {
                    group.heading ?
                        <PopoverSurface.Group
                            customTheme={customTheme}
                            heading={group.heading}
                        >
                            {rows}
                        </PopoverSurface.Group>
                    :
                        rows
                }
            </Fragment>;
        });
    };

    const renderFeedback = () => {
        if(isLoading) {
            return <PopoverSurface.Status
                customLocalize={customLocalize}
                customTheme={customTheme}
            />;
        }

        if(isMoreLoading) {
            return <PopoverSurface.Status
                customLocalize={customLocalize}
                customTheme={customTheme}
            >
                {localize("loading-more")}
            </PopoverSurface.Status>;
        }

        if(options.length) {
            return null;
        }

        return <PopoverSurface.Empty
            title={query.length ? undefined : localize("no-options")}
            searchText={query.length ? query : undefined}
            customLocalize={customLocalize}
            customTheme={customTheme}
        />;
    };

    const renderFooter = (): ReactElement | null => {
        if(!isMultipleSelect) {
            return null;
        }

        return <Fragment>
            <PopoverSurface.Separator
                customTheme={customTheme}
            />
            <View
                style={[
                    stylesheet.footer,
                    footerDynamicStyle
                ]}
            >
                <Text
                    customTheme={customTheme}
                    style={stylesheet.count}
                    variant="bodySmallSize"
                    aria-live="polite"
                    numberOfLines={1}
                    color="mid"
                >
                    {getCountText()}
                </Text>
                {
                    isShowTools ?
                        <Button
                            {...webProps}
                            isDisabled={isClearDisabled}
                            customTheme={customTheme}
                            title={localize("clear")}
                            onPress={onClear}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                    :
                        null
                }
                <Button
                    {...webProps}
                    isDisabled={!isMinChoiceMet}
                    title={localize("apply")}
                    customTheme={customTheme}
                    onPress={onApply}
                    variant="filled"
                    type="primary"
                    size="small"
                />
            </View>
        </Fragment>;
    };

    return <PopoverSurface
        initialFocusRef={isSearchable ? searchRef : undefined}
        returnFocusRef={returnFocusRef}
        isAutoFocused={isSearchable}
        customTheme={customTheme}
        onDismiss={onDismiss}
        anchorRef={anchorRef}
        minWidth="anchor"
        isOpen={isOpen}
        width="anchor"
    >
        {
            isSearchable ?
                <PopoverSurface.Search
                    aria-activedescendant={getOptionID(activeKey)}
                    onClear={() => setSearchText("")}
                    placeholder={localize("search")}
                    aria-label={localize("search")}
                    customLocalize={customLocalize}
                    onKeyPress={onSearchKeyPress}
                    onChangeText={setSearchText}
                    customTheme={customTheme}
                    aria-controls={listboxID}
                    aria-expanded={true}
                    value={searchText}
                    ref={searchRef}
                />
            :
                null
        }
        <PopoverSurface.List
            isBusy={isLoading || isMoreLoading}
            isMultiple={isMultipleSelect}
            customTheme={customTheme}
            scrollEventThrottle={16}
            onScroll={onListScroll}
            aria-label={title}
            id={listboxID}
        >
            {groupExtractor ? renderGroups() : options.map(renderOption)}
        </PopoverSurface.List>
        {renderFeedback()}
        {renderFooter()}
    </PopoverSurface>;
};
export default SelectPopover;
